import type { BaseEntity } from './BaseEntity';
import type { Batch } from './Batch';
import type { Customer } from './Customer';
import type { ProductHistoricalBulkPricingMarkupLineItem, ProductBulkPricingMarkupLineItem } from './ProductBulkPricingMarkupLineItem';
import { PackagingType, ProductInventoryItem } from './ProductInventoryItem';
import type { ProductItem } from './ProductItem';

export enum CostingType {
  STANDARD = 'STANDARD',
  BULK = 'BULK'
}

export interface Product extends BaseEntity {
  name: string;
  items: ProductItem[];
  hasInventory: boolean;
  customerId: number;
  customer?: Customer;
  costingType: CostingType;
  sku?: string | null;
  costPerUnit?: number | null;
  bulkPricingMarkupLineItems?: ProductBulkPricingMarkupLineItem[];
  historicalBulkPricingMarkupLineItems?: ProductHistoricalBulkPricingMarkupLineItem[];
  batches?: Batch[];
  ingredientItems?: ProductItem[];
  packagingItems?: ProductItem[];
  matchingProductId?: number;
  matchingProduct?: Product;
  totalItemQuantity?: number;
  totalAverageItemCostPerBatchPound?: number;
  totalAverageItemCostPerStandardBatch?: number;
  productInventoryItems?: ProductInventoryItem[];
  totalQuantityOnHand?: { key: PackagingType; value: number }[];
}

type ValidationResult = { issues: { message: string; path: (keyof Product)[], index?: number }[] };

export function validate(product: Partial<Product>): ValidationResult {
  let issues: ValidationResult['issues'] = [];

  if (!product.name) {
    issues = [...issues, { message: 'Name is required', path: ['name'] }];
  }

  if (!product.customerId) {
    issues = [...issues, { message: 'Customer is required', path: ['customerId'] }];
  }

  if (product.items) {
    for (var ingredientIndex = 0; ingredientIndex < product.items.length; ingredientIndex++) {
      if (!product.items[ingredientIndex].itemId) {
        issues = [...issues, { message: 'Item is required', path: [`items`], index: ingredientIndex }];
      }

      if (!product.items[ingredientIndex].quantity) {
        issues = [...issues, { message: 'Quantity is required', path: [`items`], index: ingredientIndex }];
      }
    }
  } else {
    issues = [...issues, { message: 'At least one item is required', path: ['items'] }];
  }

  return { issues };
}